import { CommonModule } from '@angular/common';
import { Component, inject, Inject, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { SecondsToDateSeconds } from '@vendasta/galaxy/transcript/src/transcript-entry/transcript-entry.component';
import { copyToClipboard } from '../../utils/copy';
import { MatInputModule } from '@angular/material/input';
import { MeetingAnalysisApiService, PrivacyLevel, Sharing } from '@vendasta/meeting-analysis';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { take } from 'rxjs';
import { MeetingListService } from '../../meeting-list.service';
import { MEETING_ANALYSIS_NAMESPACE_NAME_INJECTION_TOKEN$ } from '@galaxy/meeting-analysis/static';

interface DialogData {
  currentTimestamp: number;
  url: string;
  namespace: string;
  privacyLevel: PrivacyLevel;
  conversationId: string;
  sharing?: Sharing;
}

@Component({
  templateUrl: './share-dialog.component.html',
  styleUrls: ['./share-dialog.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    MatDialogModule,
    MatFormFieldModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    MatCheckboxModule,
    SecondsToDateSeconds,
    MatIcon,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    MatSlideToggleModule,
  ],
})
export class ShareDialogComponent {
  changingPrivacyLevel = false;
  namespaceName$ = inject(MEETING_ANALYSIS_NAMESPACE_NAME_INJECTION_TOKEN$);
  meetingListService = inject(MeetingListService);
  private readonly meetingAnalysisApiService = inject(MeetingAnalysisApiService);
  atCurrentTime = signal(false);
  url: string;
  currentTimestamp = signal(0);
  hourInSeconds = 3600;
  protected readonly PrivacyLevel = PrivacyLevel;
  selectedPrivacyLevel: PrivacyLevel;
  namespace = '';
  externalShareEnabled = signal(false);
  externalShareUrl = signal('');

  constructor(
    public dialogRef: MatDialogRef<ShareDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    protected readonly snackbarService: SnackbarService,
  ) {
    this.currentTimestamp.set(data.currentTimestamp);
    this.url = data.url;
    this.selectedPrivacyLevel = data.privacyLevel;
    this.namespace = data.namespace;
    const serverOn = data.sharing?.allowPublicAccess === true;
    this.externalShareEnabled.set(serverOn);
    if (serverOn) {
      this.externalShareUrl.set(this.sharingPublicUrl(data.sharing));
    }
  }

  copyInternalLink(): void {
    this.copyUrlToClipboard(this.url);
  }

  copyExternalLink(): void {
    const link = this.externalShareUrl();
    if (!link) {
      return;
    }
    this.copyUrlToClipboard(link);
  }

  onExternalShareToggle(enabled: boolean): void {
    if (enabled) {
      this.meetingAnalysisApiService
        .enablePublicSharing({ conversationId: this.data.conversationId })
        .pipe(take(1))
        .subscribe({
          next: (res) => {
            this.externalShareUrl.set(this.sharingPublicUrl(res.sharing));
            this.externalShareEnabled.set(true);
          },
          error: () => {
            this.snackbarService.openErrorSnack('COMMON.ERROR');
          },
        });
      return;
    }
    this.meetingAnalysisApiService
      .disablePublicSharing({ conversationId: this.data.conversationId })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.externalShareEnabled.set(false);
          this.externalShareUrl.set('');
          this.snackbarService.openSuccessSnack('SHARE.EXTERNAL_LINK_DISABLED');
        },
        error: () => {
          this.snackbarService.openErrorSnack('COMMON.ERROR');
        },
      });
  }

  onAtCurrentTimeChange(checked: boolean): void {
    this.atCurrentTime.set(checked);
  }

  private sharingPublicUrl(sharing: Sharing | undefined): string {
    return sharing?.publicUrl ?? '';
  }

  private copyUrlToClipboard(baseUrl: string): void {
    let urlToCopy = baseUrl;
    if (this.atCurrentTime()) {
      urlToCopy = baseUrl + '?t=' + this.currentTimestamp();
    }
    copyToClipboard(urlToCopy);
    this.snackbarService.openSuccessSnack('RECORDING.URL_COPIED');
  }

  onPrivacyLevelChange(privacyLevel: PrivacyLevel) {
    this.changingPrivacyLevel = true;
    this.meetingListService
      .updateConversationVisibility(this.data.conversationId, this.namespace, privacyLevel)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.snackbarService.openSuccessSnack('VISIBILITY.SUCCESS_CHANGING');
          this.changingPrivacyLevel = false;
        },
        error: () => {
          this.snackbarService.openErrorSnack('VISIBILITY.ERROR_CHANGING');
          this.changingPrivacyLevel = false;
        },
      });
  }
}
